import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Chip, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import { getApiErrorMessage } from '@shared/utils/apiError.utils';
import { useWorkspaceScope } from './useWorkspaceScope';
import {
  useRecordWorkspaceMissionCheckMutation,
  useWorkspaceMissionChecksQuery,
  type MissionSavedCheck,
  type MissionVersion,
} from './workspaceApi';

export const MissionSavedChecks = (props: {
  missionId: string;
  versions: MissionVersion[];
  canManage: boolean;
}) => {
  const scope = useWorkspaceScope();
  return <SavedChecksSession key={`${scope}:${props.missionId}`} {...props} />;
};

const SavedChecksSession = ({
  missionId,
  versions,
  canManage,
}: {
  missionId: string;
  versions: MissionVersion[];
  canManage: boolean;
}) => {
  const [versionId, setVersionId] = useState(versions[0]?.id ?? '');
  const [before, setBefore] = useState<string>();
  const [previous, setPrevious] = useState<MissionSavedCheck[]>([]);
  const [latest, setLatest] = useState<MissionSavedCheck | null>(null);
  const [error, setError] = useState('');
  const [working, setWorking] = useState(false);
  const [record] = useRecordWorkspaceMissionCheckMutation();
  const query = useWorkspaceMissionChecksQuery({ id: missionId, before });
  const alive = useRef(true);
  const busy = useRef(false);
  const permission = useRef(canManage);
  permission.current = canManage;
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const rows = [...previous, ...(query.currentData?.items ?? [])];
  const checks = Array.from(
    new Map([...(latest && !before ? [latest] : []), ...rows].map((row) => [row.id, row])).values(),
  );
  const inspect = async (selectedVersionId: string, retryOf?: string) => {
    if (!permission.current || busy.current || !selectedVersionId) return;
    busy.current = true;
    setWorking(true);
    setError('');
    try {
      const receipt = await record({
        id: missionId,
        versionId: selectedVersionId,
        ...(retryOf ? { retryOf } : {}),
      }).unwrap();
      if (!alive.current) return;
      setBefore(undefined);
      setPrevious([]);
      setLatest(receipt);
    } catch (e) {
      if (alive.current)
        setError(getApiErrorMessage(e, 'Unable to record this configuration check.'));
    } finally {
      busy.current = false;
      if (alive.current) setWorking(false);
    }
  };
  return (
    <Stack gap={2}>
      <Typography variant="h6">Saved configuration checks</Typography>
      <Typography variant="body2" color="text.secondary">
        Check an immutable published version against current access and configuration. This records
        evidence without running actions, calling a model or placing calls.
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
        <TextField
          label="Published version to check"
          select
          value={versions.some((version) => version.id === versionId) ? versionId : ''}
          disabled={working || !canManage}
          sx={{ minWidth: 230 }}
          onChange={(event) => setVersionId(event.target.value)}>
          {versions.map((version) => (
            <MenuItem key={version.id} value={version.id}>
              Version {version.number} · {version.spec.name}
            </MenuItem>
          ))}
        </TextField>
        <Button
          variant="outlined"
          disabled={working || !canManage || !versions.some((version) => version.id === versionId)}
          onClick={() => void inspect(versionId)}>
          Check published configuration
        </Button>
      </Stack>
      {error && <Alert severity="error">{error}</Alert>}
      {query.isError && (
        <Alert
          severity="error"
          action={<Button onClick={() => void query.refetch()}>Retry</Button>}>
          Unable to load saved configuration checks.
        </Alert>
      )}
      {!checks.length && !query.isFetching && !query.isError && (
        <Typography color="text.secondary">No saved configuration checks yet.</Typography>
      )}
      {checks.map((check) => (
        <Paper variant="outlined" key={check.id} sx={{ p: 2 }}>
          <Stack gap={1}>
            <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
              <Typography variant="subtitle2">Published version {check.versionNumber}</Typography>
              <Chip
                size="small"
                variant="outlined"
                label={
                  check.result.actions.some((action) => action.status === 'blocked')
                    ? 'Blocked'
                    : check.result.actions.some((action) => action.status === 'deferred')
                      ? 'Deferred checks'
                      : 'Configuration checked'
                }
              />
              <Typography variant="caption" color="text.secondary">
                {new Date(check.createdAt).toLocaleString()}
              </Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
              Check {check.id} · Version {check.versionId}
              {check.retryOf ? ` · Recheck of ${check.retryOf}` : ''}
            </Typography>
            {check.result.nextOccurrence && (
              <Typography variant="body2">
                Next scheduled occurrence at check time:{' '}
                {new Date(check.result.nextOccurrence).toLocaleString()}
              </Typography>
            )}
            {check.result.actions.map((action, index) => (
              <Alert
                key={index}
                severity={
                  action.status === 'blocked'
                    ? 'warning'
                    : action.status === 'deferred'
                      ? 'info'
                      : 'success'
                }>
                Action {index + 1} · {action.kind} · Campaign {action.campaignId}: {action.message}
              </Alert>
            ))}
            <Button
              disabled={working || !canManage}
              sx={{ alignSelf: 'start' }}
              aria-label={`Recheck version ${check.versionNumber} from ${check.id}`}
              onClick={() => void inspect(check.versionId, check.id)}>
              Check this version again
            </Button>
          </Stack>
        </Paper>
      ))}
      {query.currentData?.next && (
        <Button
          disabled={working || query.isFetching}
          onClick={() => {
            setPrevious(checks);
            setLatest(null);
            setBefore(query.currentData!.next!);
          }}>
          Load older checks
        </Button>
      )}
      {before && (
        <Button
          onClick={() => {
            setBefore(undefined);
            setPrevious([]);
          }}>
          Return to latest checks
        </Button>
      )}
      <Typography variant="caption" color="text.secondary">
        These are configuration checks. Provider health, credits, admission and effects are verified
        separately during execution.
      </Typography>
    </Stack>
  );
};
